/**
 * Validates a product listing form data
 * @param {Object} formData - The form data to validate
 * @returns {Object} An object with field names as keys and error messages as values
 */
export const validateListingForm = (formData) => {
  const errors = {};
  
  console.log("Validating form data:", formData);
  
  // Get product type for conditional validation
  const productType = formData.productType;
  const isOthersType = productType === 'others';
  const isVirtualFashion = productType === 'virtual';
  const isDigitalOnline = isOthersType && formData.isDigitalOnline === true;
  const isLocalService = isOthersType && formData.isLocalService === true;
  
  // Handle simplified product types (virtual or digital/online) with similar behavior
  const isSimplifiedProduct = isVirtualFashion || isDigitalOnline;
  
  // Required fields for all product types
  if (!formData.name) errors.name = 'Name is required';
  if (!formData.description) errors.description = 'Description is required';
  
  // Product Type validation - not required since we default to 'designer'
  // if (!productType) errors.productType = 'Product Type is required';
  
  // Skip detail validations for 'others' type and simplified products
  if (!isOthersType && !isVirtualFashion) {
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.size) errors.size = 'Size is required';
    if (!formData.color) errors.color = 'Color is required';
    
    // Condition is only required for closet sharing items
    if (productType === 'sharing' && !formData.condition) {
      errors.condition = 'Condition is required for closet sharing items';
    }
  } else if (isDigitalOnline) {
    // Only validate fields needed for digital/online
    // Nothing to validate specifically
  } else if (isLocalService) {
    // Validate fields needed for local services
    if (!formData.pickUpAddress && !formData.selectedAddressId) {
      errors.pickUpAddress = 'Service location is required';
    }
    if (!formData.serviceRadius) {
      errors.serviceRadius = 'Service radius is required';
    }
  } else if (isVirtualFashion) {
    // Nothing to validate specifically for virtual fashion
  }
  
  // Photos validation
  if (!formData.photos || formData.photos.length === 0) {
    errors.photos = 'At least one photo is required';
  }
  
  // Stock validation - must be greater than 0 for physical products (not for digital or local service)
  if (!isSimplifiedProduct && !isLocalService && (formData.stock === 0 || formData.stock === '0' || formData.stock === '' || !formData.stock)) {
    errors.stock = 'Stock quantity must be at least 1';
  }
  
  // Transaction type validation - simplified products are always for sell, local services always for rent
  if (!isSimplifiedProduct && !isLocalService && !formData.forSell && !formData.forRent && !formData.openToSwap) {
    errors.transactionType = 'Select at least one transaction type';
  }
  
  // Delivery options validation - Skip for simplified products and local services
  if (!isSimplifiedProduct && !isLocalService && !formData.openToShipping && !formData.openToPickUp) {
    errors.deliveryOptions = 'Select at least one delivery option (shipping or pickup)';
  }
  
  // Shipping address validation - Only if shipping is enabled
  if (!isSimplifiedProduct && !isLocalService && formData.openToShipping) {
    const hasValidShippingAddress = (formData.usePickupForShipping && formData.pickUpAddress) || 
                                   (!formData.usePickupForShipping && formData.shippingAddress);
    
    if (!hasValidShippingAddress) {
      errors.shippingAddress = 'Please select or add a shipping origin address';
    }
  }
  
  // Parcel information validation - Only if shipping is selected
  if (!isSimplifiedProduct && !isLocalService && formData.openToShipping) {
    const parcelInfo = formData.parcelInfo || {};
    const parcelErrors = {};
    
    // Helper to check if a parcel dimension is valid
    const isValidDimension = (value) => {
      // Check if value exists and is a valid positive number
      if (value === undefined || value === null || value === '') return false;
      
      // Convert to number if it's a string
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return !isNaN(numValue) && numValue > 0;
    };
    
    if (!isValidDimension(parcelInfo.length)) {
      parcelErrors.length = 'Length is required and must be greater than 0';
    }
    
    if (!isValidDimension(parcelInfo.width)) {
      parcelErrors.width = 'Width is required and must be greater than 0';
    }
    
    if (!isValidDimension(parcelInfo.height)) {
      parcelErrors.height = 'Height is required and must be greater than 0';
    }
    
    if (!isValidDimension(parcelInfo.weight)) {
      parcelErrors.weight = 'Weight is required and must be greater than 0';
    }
    
    if (Object.keys(parcelErrors).length > 0) {
      errors.parcelInfo = parcelErrors;
    }
  }
  
  // Sale price validation - REQUIRED for simplified products or if forSell is true
  if ((formData.forSell || isSimplifiedProduct) && 
      (!formData.price || formData.price === '' || 
       isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0)) {
    errors.price = 'Please enter a valid price greater than 0';
  }
  
  // Currency validation
  if ((formData.forSell || isSimplifiedProduct) && !formData.currency) {
    errors.currency = 'Currency is required';
  }
  
  // Rent prices validation - ONLY if forRent is true (or for local services)
  if (formData.forRent || isLocalService) {
    const rentPrices = formData.rentPriceAndTime || [];
    if (rentPrices.length === 0) {
      errors.rentPriceAndTime = 'Please add at least one ' + (isLocalService ? 'service rate' : 'rental period and price');
    } else {
      const hasInvalidPrices = rentPrices.some(
        rental => !rental.option || 
                  rental.price === '' || rental.price === undefined || 
                  rental.number === '' || rental.number === undefined || 
                  !rental.currency || 
                  isNaN(parseFloat(rental.price)) || parseFloat(rental.price) <= 0 ||
                  isNaN(parseFloat(rental.number)) || parseFloat(rental.number) <= 0
      );
      if (hasInvalidPrices) {
        errors.rentPriceAndTime = 'Please complete all ' + (isLocalService ? 'service rates' : 'rental periods and prices') + ' with values greater than 0';
      }
    }
  }
  
  // Pickup address validation - ONLY if openToPickUp is true (never for simplified products)
  if ((!isSimplifiedProduct && formData.openToPickUp) || isLocalService) {
    // If using shipping address for pickup, validate that shipping address exists
    if (formData.useShippingForPickup) {
      if (!formData.shippingAddress) {
        errors.pickUpAddress = 'Please select a valid shipping address to use for pickup';
      }
    } else if (!formData.pickUpAddress && !formData.selectedAddressId) {
      // Otherwise validate that pickup address exists
      errors.pickUpAddress = 'Please select or add a ' + (isLocalService ? 'service location' : 'pickup address');
    }
  }
  
  console.log("Validation errors:", errors);
  
  return errors;
};

/**
 * Check if an object is empty
 * @param {Object} obj - The object to check
 * @returns {boolean} True if the object is empty, false otherwise
 */
export const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0;
};

/**
 * Validate a number value
 * @param {any} value - The value to check
 * @returns {boolean} True if the value is a valid number, false otherwise
 */
export const isNumber = (value) => {
  if (value === '' || value === null || value === undefined) return false;
  const num = Number(value);
  return !isNaN(num) && typeof num === 'number';
}; 